import { parseFitnessDraftRestoreRaw } from '@/lib/fitnessDraftRestoreCodec';

export interface FitnessDraftDurableDriver {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}
export interface FitnessDraftPersistenceStatus {
  readonly status: 'idle' | 'hydrating' | 'pending' | 'saved' | 'failed';
  readonly revision: number;
  readonly operation: 'write' | 'remove' | 'hydrate' | null;
  readonly errorCode: string | null;
}
export type FitnessDraftCheckpointRead = { kind: 'checkpoint'; raw: string; clientId: string } | { kind: 'tombstone'; clientId: string } | null;
interface RecordValue { version: 1; ownerId: string; clientId: string; revision: number; kind: 'checkpoint' | 'tombstone'; raw?: string }
type Intent = { kind: 'write'; raw: string } | { kind: 'remove'; clientId: string } | { kind: 'hydrate' };
const IDLE: FitnessDraftPersistenceStatus = Object.freeze({ status: 'idle', revision: 0, operation: null, errorCode: null });
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const fail = (code: string): never => { throw new Error(code); };
const ownerKey = (owner: string): string => {
  if (!UUID.test(owner)) return fail('fitness_draft_invalid_owner');
  return 'vyv_fitness_checkpoint:v1:' + owner;
};
function code(error: unknown): string {
  return error instanceof Error && /^fitness_draft_[a-z_]+$/.test(error.message) ? error.message : 'fitness_draft_storage_unavailable';
}
function parseRecord(raw: string | null, owner: string): FitnessDraftCheckpointRead {
  if (raw === null) return null;
  if (raw.length > 1100000) return fail('fitness_draft_checkpoint_invalid');
  let value: RecordValue;
  try { value = JSON.parse(raw) as RecordValue; } catch { return fail('fitness_draft_checkpoint_invalid'); }
  if (!value || typeof value !== 'object' || Array.isArray(value) || value.version !== 1 || value.ownerId !== owner || !UUID.test(value.clientId)
    || !Number.isSafeInteger(value.revision) || value.revision < 1
    || Object.keys(value).some(key => !['version', 'ownerId', 'clientId', 'revision', 'kind', 'raw'].includes(key))) return fail('fitness_draft_checkpoint_invalid');
  if (value.kind === 'tombstone') {
    if (Object.prototype.hasOwnProperty.call(value, 'raw')) return fail('fitness_draft_checkpoint_invalid');
    return { kind: 'tombstone', clientId: value.clientId };
  }
  if (value.kind !== 'checkpoint' || typeof value.raw !== 'string') return fail('fitness_draft_checkpoint_invalid');
  const state = parseFitnessDraftRestoreRaw(value.raw, owner);
  if (state.clientId !== value.clientId) return fail('fitness_draft_checkpoint_invalid');
  return { kind: 'checkpoint', raw: value.raw, clientId: value.clientId };
}

/** Acknowledged checkpoint queue. A timeout never releases a still-running native
 * operation: subsequent writes/tombstones remain behind it and cannot be undone
 * by a late acknowledgement. This is separate from same-context workout CAS. */
export function createFitnessDraftPersistenceCore(options: { driver: FitnessDraftDurableDriver; timeoutMs?: number }) {
  const timeoutMs = options.timeoutMs ?? 5000;
  const statuses = new Map<string, FitnessDraftPersistenceStatus>();
  const tails = new Map<string, Promise<void>>();
  const intents = new Map<string, Intent>();
  const retired = new Map<string, Set<string>>();
  const listeners = new Set<() => void>();
  const getStatus = (owner: string | null): FitnessDraftPersistenceStatus => owner ? statuses.get(owner) ?? IDLE : IDLE;
  const publish = (owner: string, value: FitnessDraftPersistenceStatus) => {
    statuses.set(owner, Object.freeze(value));
    for (const listener of listeners) { try { listener(); } catch { /* A subscriber cannot prevent a checkpoint write. */ } }
  };
  const isCurrent = (guard: () => boolean) => { if (!guard()) fail('fitness_draft_owner_changed'); };
  const next = (owner: string, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>) => {
    ownerKey(owner);
    const revision = getStatus(owner).revision + 1;
    publish(owner, { status: operation === 'hydrate' ? 'hydrating' : 'pending', revision, operation, errorCode: null });
    return revision;
  };
  const failed = (owner: string, revision: number, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>, error: unknown) => {
    if (getStatus(owner).revision === revision) publish(owner, { status: 'failed', revision, operation, errorCode: code(error) });
  };
  const saved = (owner: string, revision: number, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>) => {
    if (getStatus(owner).revision === revision) publish(owner, { status: 'saved', revision, operation, errorCode: null });
  };
  const enqueue = <T,>(owner: string, revision: number, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>, work: () => Promise<T>): Promise<T> => {
    const previous = tails.get(owner) ?? Promise.resolve();
    const actual = previous.then(work);
    // Only settlement, never the timeout, advances the real native queue.
    tails.set(owner, actual.then(() => undefined, () => undefined));
    const timer = setTimeout(() => failed(owner, revision, operation, new Error('fitness_draft_storage_timeout')), timeoutMs);
    void actual.then(() => clearTimeout(timer), error => { clearTimeout(timer); failed(owner, revision, operation, error); });
    return actual;
  };
  const bounded = async <T,>(owner: string, revision: number, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>, actual: Promise<T>): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('fitness_draft_storage_timeout')), timeoutMs); });
    try { return await Promise.race([actual, timeout]); }
    catch (error) { failed(owner, revision, operation, error); throw error; }
    finally { clearTimeout(timer); }
  };
  const write = (owner: string, raw: string): void => {
    const key = ownerKey(owner), draft = parseFitnessDraftRestoreRaw(raw, owner);
    if (retired.get(owner)?.has(draft.clientId)) return fail('fitness_draft_retired');
    intents.set(owner, { kind: 'write', raw });
    const revision = next(owner, 'write');
    const encoded = JSON.stringify({ version: 1, ownerId: owner, clientId: draft.clientId, revision, kind: 'checkpoint', raw } satisfies RecordValue);
    void enqueue(owner, revision, 'write', async () => {
      // Coalesce superseded typing/phase changes before reaching the bridge.
      if (getStatus(owner).revision !== revision || retired.get(owner)?.has(draft.clientId)) return;
      const existing = parseRecord(await options.driver.getItem(key), owner);
      if (existing?.kind === 'tombstone' && existing.clientId === draft.clientId) return fail('fitness_draft_retired');
      if (existing?.kind === 'checkpoint' && existing.clientId !== draft.clientId) return fail('fitness_draft_checkpoint_exists');
      if (getStatus(owner).revision !== revision || retired.get(owner)?.has(draft.clientId)) return;
      await options.driver.setItem(key, encoded);
      if (await options.driver.getItem(key) !== encoded) fail('fitness_draft_readback_mismatch');
      saved(owner, revision, 'write');
    }).catch(() => undefined);
  };
  const read = async (owner: string, guard: () => boolean): Promise<FitnessDraftCheckpointRead> => {
    const key = ownerKey(owner); isCurrent(guard);
    // A read must never supersede or coalesce away a queued checkpoint write.
    const before = getStatus(owner), revision = before.revision;
    const standaloneRead = !intents.has(owner) || intents.get(owner)?.kind === 'hydrate';
    if (standaloneRead) { intents.set(owner, { kind: 'hydrate' }); publish(owner, { status: 'hydrating', revision, operation: 'hydrate', errorCode: null }); }
    const value = await bounded(owner, revision, 'hydrate', enqueue(owner, revision, 'hydrate', async () => {
      isCurrent(guard); const result = parseRecord(await options.driver.getItem(key), owner); isCurrent(guard);
      if (getStatus(owner).revision !== revision) return fail('fitness_draft_changed');
      if (result?.kind === 'tombstone') { const ids = retired.get(owner) ?? new Set<string>(); ids.add(result.clientId); retired.set(owner, ids); }
      if (standaloneRead && result) saved(owner, revision, 'hydrate');
      else if (standaloneRead && getStatus(owner).revision === revision) publish(owner, { status: 'idle', revision, operation: null, errorCode: null });
      return result;
    }));
    isCurrent(guard); return value;
  };
  const retire = async (owner: string, clientId: string, guard: () => boolean): Promise<void> => {
    const key = ownerKey(owner); if (!UUID.test(clientId)) return fail('fitness_draft_checkpoint_invalid'); isCurrent(guard);
    const ids = retired.get(owner) ?? new Set<string>(), previouslyRetired = ids.has(clientId); ids.add(clientId); retired.set(owner, ids);
    intents.set(owner, { kind: 'remove', clientId }); const revision = next(owner, 'remove'); let mutationStarted = false;
    const encoded = JSON.stringify({ version: 1, ownerId: owner, clientId, revision, kind: 'tombstone' } satisfies RecordValue);
    const actual = enqueue(owner, revision, 'remove', async () => {
      try {
        isCurrent(guard); const existing = parseRecord(await options.driver.getItem(key), owner); isCurrent(guard);
        if (existing?.kind === 'checkpoint' && existing.clientId !== clientId) return fail('fitness_draft_changed');
        mutationStarted = true; await options.driver.setItem(key, encoded);
        if (await options.driver.getItem(key) !== encoded) return fail('fitness_draft_readback_mismatch');
        saved(owner, revision, 'remove');
      } catch (error) { if (!mutationStarted && !previouslyRetired) ids.delete(clientId); throw error; }
    });
    await bounded(owner, revision, 'remove', actual); isCurrent(guard);
  };
  const flush = async (owner: string, guard: () => boolean): Promise<void> => {
    ownerKey(owner); isCurrent(guard); const captured = getStatus(owner), operation = captured.operation ?? 'write';
    await bounded(owner, captured.revision, operation, tails.get(owner) ?? Promise.resolve()); isCurrent(guard);
    const current = getStatus(owner);
    if (current.revision !== captured.revision) return fail('fitness_draft_changed');
    if (current.status === 'failed') return fail(current.errorCode ?? 'fitness_draft_storage_unavailable');
    if (!['idle', 'saved'].includes(current.status)) return fail('fitness_draft_storage_unconfirmed');
  };
  const retry = async (owner: string, guard: () => boolean): Promise<void> => {
    ownerKey(owner); isCurrent(guard); const intent = intents.get(owner);
    if (intent?.kind === 'write') { write(owner, intent.raw); await flush(owner, guard); }
    else if (intent?.kind === 'remove') await retire(owner, intent.clientId, guard);
    else await read(owner, guard);
  };
  const reportFailure = (owner: string, operation: NonNullable<FitnessDraftPersistenceStatus['operation']>, errorCode: string) => {
    ownerKey(owner); const revision = next(owner, operation); failed(owner, revision, operation, new Error(errorCode));
  };
  const markPending = (owner: string): void => { next(owner, 'write'); };
  return { write, read, retire, flush, retry, getStatus, reportFailure, markPending, subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; } };
}
